/**
 * Менеджер для управления кнопкой админ-панели
 */

/**
 * Создаёт менеджер кнопки админ-панели
 * @param {Object} options
 * @param {Function} options.getCurrentUser - Функция для получения текущего пользователя
 * @param {Function} options.getTelegramWebApp - Функция для получения Telegram WebApp
 * @param {Function} options.showScreen - Функция для показа экрана
 * @param {Function} options.checkAdminStatus - Функция для проверки админ-статуса через API (опционально)
 * @returns {Object} Менеджер с методами ensureButton, checkAndShow
 */
export function createAdminButtonManager({
  getCurrentUser,
  getTelegramWebApp,
  showScreen,
  checkAdminStatus = null
}) {
  let adminStatusChecked = false;
  let isAdmin = false;

  /**
   * Проверяет, является ли пользователь админом через API
   * @returns {Promise<boolean>}
   */
  async function checkIsAdmin() {
    // Если уже проверяли - возвращаем кешированный результат
    if (adminStatusChecked) {
      return isAdmin;
    }

    // Если есть функция проверки через API - используем её
    if (checkAdminStatus && typeof checkAdminStatus === 'function') {
      try {
        isAdmin = await checkAdminStatus();
        adminStatusChecked = true;
        return isAdmin;
      } catch (error) {
        console.error('[Admin Button] Error checking admin status:', error);
        isAdmin = false;
        adminStatusChecked = true;
        return false;
      }
    }

    // Fallback: если функция не передана, возвращаем false
    console.warn('[Admin Button] checkAdminStatus function not provided');
    return false;
  }

  /**
   * Создаёт кнопку админ-панели
   * @param {HTMLElement} parentElement - Родительский элемент для вставки кнопки
   * @returns {HTMLElement} Созданная кнопка
   */
  function createAdminButton(parentElement) {
    const btn = document.createElement('button');
    btn.id = 'admin-button';
    btn.className = 'btn btn-secondary';
    btn.style.cssText = 'width: 100%; margin-top: 8px;';
    btn.textContent = '⚙️ Админ-панель';
    btn.onclick = function() {
      try {
        if (typeof showScreen === 'function') {
          showScreen('admin');
        } else if (window.showScreen) {
          window.showScreen('admin');
        }
      } catch(e) {
        console.error('[Admin Button] Error showing admin screen:', e);
      }
    };
    
    if (parentElement) {
      parentElement.appendChild(btn);
    }
    
    return btn;
  }

  /**
   * Находит кнопку достижений для использования как референс
   * @returns {HTMLElement|null}
   */
  function findAchievementsButton() {
    return Array.from(document.querySelectorAll('button'))
      .find(b => (b.getAttribute('onclick') || '').includes("showAchievements"));
  }

  // Флаг для предотвращения множественных проверок
  let isChecking = false;
  let lastCheckTime = 0;
  const CHECK_THROTTLE_MS = 2000; // Проверяем не чаще раза в 2 секунды
  
  /**
   * Обеспечивает наличие и правильное отображение кнопки админ-панели
   * @returns {Promise<boolean>} true если кнопка успешно создана/показана, false если нужно повторить позже
   */
  async function ensureButton() {
    // Throttle проверки
    const now = Date.now();
    if (isChecking || (now - lastCheckTime) < CHECK_THROTTLE_MS) {
      return false;
    }
    
    isChecking = true;
    lastCheckTime = now;
    
    try {
      const adminButton = document.getElementById('admin-button');
      const achievementsBtn = findAchievementsButton();
      
      if (!achievementsBtn) {
        // Кнопка достижений ещё не загружена, попробуем позже
        isChecking = false;
        return false;
      }
      
      // Если кнопка уже существует, просто обновляем её видимость
      if (adminButton) {
        const userIsAdmin = await checkIsAdmin();
        if (userIsAdmin) {
          adminButton.style.display = 'block';
          adminButton.style.visibility = 'visible';
        } else {
          adminButton.style.display = 'none';
          adminButton.style.visibility = 'hidden';
        }
        isChecking = false;
        return true;
      }
      
      // Проверяем админ-статус через API только если кнопки нет
      const userIsAdmin = await checkIsAdmin();
      
      // Логируем только при создании новой кнопки
      if (userIsAdmin && !adminButton && achievementsBtn && achievementsBtn.parentElement) {
        createAdminButton(achievementsBtn.parentElement);
        console.log('[Admin Button] ✅ Button created and inserted');
        isChecking = false;
        return true;
      } else if (adminButton) {
        adminButton.style.display = userIsAdmin ? 'block' : 'none';
        adminButton.style.visibility = userIsAdmin ? 'visible' : 'hidden';
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('[Admin Button] Error:', error);
      return false;
    }
  }

  /**
   * Проверяет и показывает кнопку с несколькими попытками
   * @param {Object} options
   * @param {number} options.maxAttempts - Максимальное количество попыток (по умолчанию 10)
   * @param {number} options.interval - Интервал между попытками в мс (по умолчанию 300)
   * @param {Function} options.onComplete - Callback при завершении (опционально)
   */
  function checkAndShow({ maxAttempts = 10, interval = 300, onComplete = null } = {}) {
    let attempts = 0;
    const checkInterval = setInterval(async () => {
      attempts++;
      const result = await ensureButton();
      if (result || attempts >= maxAttempts) {
        clearInterval(checkInterval);
        if (onComplete && typeof onComplete === 'function') {
          onComplete(attempts >= maxAttempts);
        }
      }
    }, interval);
  }

  return {
    ensureButton,
    checkAndShow,
    checkIsAdmin
  };
}



